import { useState, useCallback } from "react";
import s from "../../../wizard.module.css";
import { Section, TextField, SelectField } from "../../../FieldHelpers";
import { REGEX_PLANO_Z } from "../../../../services/validationRules";
import apiFetch from "../../../../../../shared/services/apiClient";
import { useEsCliente } from "../../../../../../shared/hooks/useTenantTipo";

const FIELD_LABELS = {
  idFilial: "ID de Filial",
  numPlano: "Número de Plano",
  numFolio: "Número de Folio",
};

// ── Hook: verifica si un campo ya existe en el condominio ──────────────────
function useUniquenessCheck() {
  const [checking, setChecking] = useState({});
  const [dupErrors, setDupErrors] = useState({});

  const check = useCallback(async (field, value, excluir) => {
    if (!value || value.trim() === "") return;
    setChecking((prev) => ({ ...prev, [field]: true }));
    try {
      const extra = excluir ? `&excluir=${excluir}` : "";
      const res = await apiFetch(
        `/api/residents/filial-check/?${field}=${encodeURIComponent(value.trim())}${extra}`,
      );
      const data = await res.json();
      setDupErrors((prev) => ({
        ...prev,
        [field]: data[field]
          ? `Este ${FIELD_LABELS[field]} ya está registrado en el condominio.`
          : null,
      }));
    } catch (_) {
      // Si falla el check no bloquear — el backend lo rechazará al guardar
    } finally {
      setChecking((prev) => ({ ...prev, [field]: false }));
    }
  }, []);

  return {
    check,
    checking,
    dupErrors,
    clearDup: (field) => setDupErrors((prev) => ({ ...prev, [field]: null })),
  };
}

export default function Filial({ formData, setFormData, errors = {} }) {
  const esCliente = useEsCliente();
  const { check: checkBase, checking, dupErrors, clearDup } = useUniquenessCheck();
  // Al editar, la filial actual no cuenta como duplicado de sí misma.
  const check = (field, value) => checkBase(field, value, formData.filial?.id);
  const [autoGenerado, setAutoGenerado] = useState(
    REGEX_PLANO_Z.test((formData.filial?.numPlano || "").trim()),
  );
  const [generando, setGenerando] = useState(false);

  function handleChange(field, value) {
    setFormData((prev) => ({
      ...prev,
      filial: { ...prev.filial, [field]: value },
    }));
    clearDup(field);
  }

  // Plano y folio son texto libre: hay planos y folios reales que no siguen
  // el formato típico, así que no se fuerza ninguna máscara.
  async function generarCodigoAuto() {
    setGenerando(true);
    try {
      const res = await apiFetch("/api/residents/filial/siguiente-sin-plano/");
      const data = await res.json();
      handleChange("numPlano", data.numPlano);
      setAutoGenerado(true);
      clearDup("numPlano");
    } catch (_) {
      // Si falla, el usuario puede intentar de nuevo
    } finally {
      setGenerando(false);
    }
  }

  function limpiarPlano() {
    handleChange("numPlano", "");
    setAutoGenerado(false);
    clearDup("numPlano");
  }

  const lleno = (campo) => (formData.filial?.[campo] || "").trim() !== "";
  // Error de validación del paso + error de duplicado del mismo campo
  const err = (campo) => errors[campo] || dupErrors[campo];
  const ok = (campo) => lleno(campo) && !err(campo) && !checking[campo];

  // ── Modo cliente: solo el ID/código del cliente ───────────────────────────
  if (esCliente) {
    return (
      <Section
        title="Identificación del Cliente"
        subtitle="Asigná un código o identificador único para este cliente."
      >
        <TextField
          name="idFilial"
          label="ID / Código de Cliente"
          required
          placeholder="Ej: CLI-001"
          help="Identificador único del cliente dentro de tu organización."
          value={formData.filial.idFilial}
          onChange={(e) => handleChange("idFilial", e.target.value)}
          onBlur={() => check("idFilial", formData.filial.idFilial)}
          error={err("idFilial")}
          ok={ok("idFilial")}
          busy={checking.idFilial}
        />
      </Section>
    );
  }

  return (
    <>
      <Section
        title="Información Básica"
        subtitle="Identificador de la unidad dentro del complejo y su condición actual."
      >
        <div className={s.grid2}>
          <TextField
            name="idFilial"
            label="ID Filial"
            required
            placeholder="Ej: AB2"
            value={formData.filial.idFilial}
            onChange={(e) => handleChange("idFilial", e.target.value)}
            onBlur={() => check("idFilial", formData.filial.idFilial)}
            error={err("idFilial")}
            ok={ok("idFilial")}
            busy={checking.idFilial}
          />

          <SelectField
            name="tipoPropiedad"
            label="Tipo de Propiedad"
            required
            value={formData.filial.tipoPropiedad}
            onChange={(e) => handleChange("tipoPropiedad", e.target.value)}
            error={errors.tipoPropiedad}
          >
            <option value="">- Seleccioná el tipo -</option>
            <option value="Construida">Construida</option>
            <option value="Lote Vacio">Lote vacío</option>
            <option value="En Construccion">En construcción</option>
          </SelectField>
        </div>
      </Section>

      <Section
        title="Registro Catastral"
        subtitle="Información registral ante el Registro Nacional de la Propiedad."
      >
        <div className={s.grid2}>
          <TextField
            name="numPlano"
            label="Número Plano"
            optional
            hint={
              autoGenerado ? (
                <button type="button" className={`${s.linkBtn} ${s.linkBtnMuted}`} onClick={limpiarPlano}>
                  <i className="fa-solid fa-xmark" /> Limpiar
                </button>
              ) : (
                <button
                  type="button"
                  className={s.linkBtn}
                  onClick={generarCodigoAuto}
                  disabled={generando}
                  title="Generar un código automático para una filial sin plano catastrado"
                >
                  {generando ? <i className="fa-solid fa-circle-notch fa-spin" /> : null} Generar automático
                </button>
              )
            }
            // Solo se explica cuando el código es automático: el campo queda
            // bloqueado y conviene decir por qué.
            help={autoGenerado ? "Código automático: esta filial no tiene plano catastrado." : undefined}
            placeholder="Ej: A-10101-2026"
            maxLength={150}
            value={formData.filial.numPlano}
            onChange={(e) => handleChange("numPlano", e.target.value)}
            onBlur={() => {
              if (lleno("numPlano") && !autoGenerado) check("numPlano", formData.filial.numPlano);
            }}
            error={err("numPlano")}
            ok={ok("numPlano")}
            busy={checking.numPlano}
            locked={autoGenerado}
          />

          <TextField
            name="numFolio"
            label="Número Folio"
            required
            placeholder="Ej: 1-10101-F-000"
            maxLength={150}
            value={formData.filial.numFolio}
            onChange={(e) => handleChange("numFolio", e.target.value)}
            onBlur={() => {
              if (lleno("numFolio")) check("numFolio", formData.filial.numFolio);
            }}
            error={err("numFolio")}
            ok={ok("numFolio")}
            busy={checking.numFolio}
          />
        </div>
      </Section>
    </>
  );
}
